import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { PreviewModal } from '../../components/admin/templates/PreviewModal';
import { TemplateForm } from '../../components/admin/templates/TemplateForm';
import { TemplateList } from '../../components/admin/templates/TemplateList';
import { PillButton } from '../../components/clients/PillButton';
import { Input, Select } from '../../components/clients/form-primitives';
import { PageHelp } from '../../components/common/PageHelp';
import { useAuth } from '../../hooks/use-auth';
import { useTemplate, useTemplates } from '../../hooks/use-notification-templates';

type ChannelFilter = 'all' | 'whatsapp' | 'email';
type StatusFilter = 'all' | 'active' | 'inactive';

export function AdminNotificationTemplatesPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [keySearch, setKeySearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  const listFilter = useMemo(
    () => ({
      channel: channelFilter === 'all' ? undefined : channelFilter,
    }),
    [channelFilter],
  );
  const query = useTemplates(listFilter);
  const editingDetail = useTemplate(formMode === 'edit' ? editingId : null);
  const previewingDetail = useTemplate(previewingId);

  const filteredItems = useMemo(() => {
    const items = query.data?.items ?? [];
    const lowerSearch = keySearch.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter === 'active' && !item.isActive) return false;
      if (statusFilter === 'inactive' && item.isActive) return false;
      if (lowerSearch && !item.key.toLowerCase().includes(lowerSearch)) return false;
      return true;
    });
  }, [query.data, keySearch, statusFilter]);

  function handleOpenCreate() {
    setFormMode('create');
    setEditingId(null);
    setFormOpen(true);
  }

  function handleOpenEdit(id: string) {
    setFormMode('edit');
    setEditingId(id);
    setFormOpen(true);
  }

  function handleOpenPreview(id: string) {
    setPreviewingId(id);
    setPreviewOpen(true);
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">
              Templates de notificação
            </h1>
            <p className="mt-1 text-sm text-muted">
              CRUD admin · cada edição cria nova versão preservando histórico
            </p>
          </div>
          <PageHelp id="admin-templates" />
        </div>
        {isAdmin && (
          <PillButton type="button" solid onClick={handleOpenCreate}>
            <Plus size={14} strokeWidth={2} />
            Novo template
          </PillButton>
        )}
        {!isAdmin && (
          <span
            className="rounded-[6px] border border-line bg-panel-2 px-3 py-1 text-[12px] text-muted"
            title="Apenas admin"
          >
            Somente leitura
          </span>
        )}
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="relative">
          <Search
            size={14}
            strokeWidth={1.8}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-3"
          />
          <Input
            value={keySearch}
            onChange={(e) => setKeySearch(e.target.value)}
            placeholder="Buscar por key…"
            className="pl-9"
          />
        </div>
        <Select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value as ChannelFilter)}
        >
          <option value="all">Todos os canais</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
        </Select>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="all">Ativos + inativos</option>
          <option value="active">Apenas ativos</option>
          <option value="inactive">Apenas inativos</option>
        </Select>
        <div className="text-right text-[12px] text-muted">
          {query.isFetching ? 'Atualizando…' : `${filteredItems.length} templates`}
        </div>
      </div>

      {query.isLoading && (
        <div className="rounded-[12px] border border-line bg-panel py-12 text-center text-sm text-muted">
          Carregando templates…
        </div>
      )}

      {query.isError && (
        <div className="rounded-[12px] border border-bad/40 bg-bad/10 p-4 text-sm text-ink">
          Erro ao carregar templates. Tente recarregar a página.
        </div>
      )}

      {query.data && (
        <TemplateList
          items={filteredItems}
          canEdit={isAdmin}
          onEdit={handleOpenEdit}
          onPreview={handleOpenPreview}
        />
      )}

      {/* Form drawer/modal (create + edit) */}
      <TemplateForm
        mode={formMode}
        template={formMode === 'edit' ? (editingDetail.data ?? null) : null}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingId(null);
        }}
      />

      {/* Preview modal */}
      <PreviewModal
        template={previewingDetail.data ?? null}
        open={previewOpen}
        onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) setPreviewingId(null);
        }}
      />
    </div>
  );
}
